/**
 * Orchestrates a fix session: read the roster, ask each breach's strategy for
 * proposals, dry-run every proposal in the database, and rank what works.
 *
 * The engine depends only on the gateway port and the strategies it is given,
 * so it runs the same against Supabase or a test double.
 */

import { mergeLicenceLookups, rankCandidates } from './candidateRanking';
import { uniqueViolations, violationIdentity } from './rosterModel';
import { createStrategyRegistry, defaultStrategies } from './strategies';
import type {
  ApplyResponse,
  CandidateWindow,
  ComplianceIssue,
  FixOption,
  FixVerification,
  OptionPreviewRequest,
  OptionPreviewResult,
  ProposedFix,
  RankedCandidate,
  RemediationContext,
  RemediationStrategy,
  RosterChangeOperation,
  RosterComplianceGateway,
  RosterRule,
  RosterRuleViolation,
  RosterSite,
  RosterSnapshot,
  UnsupportedReason,
} from './types';
import { classifyPreview, isOfferable, verificationRank } from './verification';

/** `preview_roster_fix_options` refuses larger batches. */
export const PREVIEW_BATCH_SIZE = 40;

export interface RemediationPlan {
  snapshot: RosterSnapshot;
  rule: RosterRule | null;
  baseline: RosterRuleViolation[];
  issues: ComplianceIssue[];
  /** Names of every guard a suggestion mentions, including ones not yet on this roster. */
  guardNames: Record<string, string>;
}

export interface RemediationEngine {
  suggest(site: RosterSite, slotDate: string): Promise<RemediationPlan>;
  previewChanges(plan: RemediationPlan, operations: RosterChangeOperation[]): Promise<FixVerification>;
  applyChanges(plan: RemediationPlan, operations: RosterChangeOperation[]): Promise<ApplyResponse>;
}

export interface RemediationEngineDependencies {
  gateway: RosterComplianceGateway;
  strategies?: readonly RemediationStrategy[];
}

const operationsKey = (operations: RosterChangeOperation[]): string => JSON.stringify(operations);

/** Options that clear their breach outrank ones that trade it for another; then full cover, then the strategy's order. */
function compareOptions(a: FixOption, b: FixOption): number {
  return verificationRank(a.verification.status) - verificationRank(b.verification.status)
    || a.uncoveredMinutes - b.uncoveredMinutes
    || a.preference - b.preference
    || (b.candidate?.score ?? 0) - (a.candidate?.score ?? 0);
}

async function previewInBatches(
  gateway: RosterComplianceGateway,
  siteId: string,
  slotDate: string,
  requests: OptionPreviewRequest[],
): Promise<Map<string, OptionPreviewResult>> {
  const results = new Map<string, OptionPreviewResult>();
  for (let start = 0; start < requests.length; start += PREVIEW_BATCH_SIZE) {
    const response = await gateway.previewOptions(siteId, slotDate, requests.slice(start, start + PREVIEW_BATCH_SIZE));
    response.options.forEach((result) => results.set(result.key, result));
  }
  return results;
}

export function createRemediationEngine({
  gateway,
  strategies = defaultStrategies,
}: RemediationEngineDependencies): RemediationEngine {
  const registry = createStrategyRegistry(strategies);

  return {
    async suggest(site, slotDate) {
      const [snapshot, rule, evaluation] = await Promise.all([
        gateway.loadSnapshot(site, slotDate),
        gateway.loadRule(site.countryCode),
        gateway.previewOptions(site.id, slotDate, []),
      ]);

      // Work from the roster as it is now, not the list the popup was opened with.
      const baseline = uniqueViolations(evaluation.baseline);
      const guardNames: Record<string, string> = {};
      const candidateCache = new Map<string, Promise<RankedCandidate[]>>();

      const findCandidates = (window: CandidateWindow): Promise<RankedCandidate[]> => {
        const cacheKey = JSON.stringify([
          window.startMinute,
          window.endMinute,
          [...window.requiredLicenceTypeIds].sort(),
          [...window.excludeGuardIds].sort(),
        ]);
        let pending = candidateCache.get(cacheKey);
        if (!pending) {
          const licences = window.requiredLicenceTypeIds.length > 0 ? window.requiredLicenceTypeIds : [null];
          pending = Promise.all(licences.map((requiredLicenceTypeId) => gateway.findCandidates(site.id, slotDate, {
            startMinute: window.startMinute,
            endMinute: window.endMinute,
            requiredLicenceTypeId,
            excludeGuardIds: window.excludeGuardIds,
          }))).then((lookups) => {
            const guards = mergeLicenceLookups(lookups);
            guards.forEach((guard) => { guardNames[guard.guardId] = guard.fullName; });
            return rankCandidates(guards, { window, rule });
          });
          candidateCache.set(cacheKey, pending);
        }
        return pending;
      };

      const context: RemediationContext = { snapshot, rule, violations: baseline, findCandidates };

      const proposalsByIssue = await Promise.all(baseline.map(async (violation, index) => {
        const key = `${violationIdentity(violation)}#${index}`;
        const strategy = registry.resolve(violation.rule_code);
        if (!strategy) {
          return { key, violation, proposals: [] as ProposedFix[], unsupportedReason: 'no_strategy' as UnsupportedReason };
        }
        const result = await strategy.propose(violation, context);
        return { key, violation, proposals: result.proposals, unsupportedReason: result.unsupportedReason ?? null };
      }));

      // Different breaches often propose the same change (one reassignment can
      // cure a guard's rest and hours together); each distinct set is checked once.
      const requests = new Map<string, OptionPreviewRequest>();
      proposalsByIssue.forEach(({ proposals }) => proposals.forEach((proposal) => {
        const key = operationsKey(proposal.operations);
        if (!requests.has(key)) requests.set(key, { key: `option-${requests.size + 1}`, operations: proposal.operations });
      }));

      const results = await previewInBatches(gateway, site.id, slotDate, Array.from(requests.values()));

      const issues = proposalsByIssue.map(({ key, violation, proposals, unsupportedReason }): ComplianceIssue => {
        const options = proposals
          .map((proposal, index): FixOption => {
            const request = requests.get(operationsKey(proposal.operations));
            const result = request ? results.get(request.key) : undefined;
            return {
              ...proposal,
              id: `${key}:${index}`,
              issueKey: key,
              verification: classifyPreview(violation, baseline, result?.violations ?? null, result ? result.error : 'Not previewed'),
            };
          })
          .filter((option) => isOfferable(option.verification))
          .sort(compareOptions);

        const recommended = options.find((option) => option.verification.status === 'resolves') ?? null;
        return {
          key,
          violation,
          options,
          recommendedOptionId: recommended?.id ?? null,
          unsupportedReason: options.length > 0
            ? null
            : unsupportedReason ?? (proposals.length > 0 ? 'no_verified_fix' : 'no_eligible_guards'),
        };
      });

      // Guards already on the roster are named by their assignments.
      [...snapshot.slots, ...snapshot.breakSlots].forEach((slot) => slot.assignments.forEach((assignment) => {
        if (!guardNames[assignment.guard_id]) guardNames[assignment.guard_id] = assignment.guard_name;
      }));

      return { snapshot, rule, baseline, issues, guardNames };
    },

    async previewChanges(plan, operations) {
      const response = await gateway.previewOptions(plan.snapshot.site.id, plan.snapshot.slotDate, [
        { key: 'selection', operations },
      ]);
      const result = response.options.find((option) => option.key === 'selection');
      return classifyPreview(null, uniqueViolations(response.baseline), result?.violations ?? null, result?.error ?? null);
    },

    async applyChanges(plan, operations) {
      return gateway.applyOperations(plan.snapshot.site.id, plan.snapshot.slotDate, operations);
    },
  };
}
